import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../api/axios";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";
import SignTableDocCreator from "../../components/approval/SignTable_doc_creator";
import SignTableDocSingle from "../../components/approval/SignTable_doc_single";
import { getMaterialDoc } from "../../api/material/meta";

export default function MaterialQCView({ data }) {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  const rawLines = data?.lines || [];
  const dns = data?.dns || data?.doc_dns;

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  /* ===============================
     담당자 정규화
  =============================== */
  const rawAssigned =
    data?.header?.assigned_people ?? data?.assigned_people ?? [];

  const assignedIds = rawAssigned
    .map((p) => {
      if (typeof p === "string" || typeof p === "number") return String(p);
      if (typeof p === "object") return String(p.id || p._id);
      return null;
    })
    .filter(Boolean);

  /* ===============================
     체크리스트 생성 권한
     👉 assigned 사람만 가능
  =============================== */
  const canCreateCK =
    user.is_superuser || assignedIds.includes(String(user.id));

  /* ===============================
     QC 라인 정규화
  =============================== */
  const items = rawLines
    .map((line) => {
      const body = line.body || {};

      return {
        code: body.code ?? line.code ?? "",
        name: body.name ?? line.name ?? line.title ?? "",
        spec: body.spec ?? line.spec ?? "",
        unit: body.unit ?? line.unit ?? "",
        qty: body.qty ?? line.qty ?? "",
        maker: body.maker ?? line.maker ?? "",
        remark: body.remark ?? line.remark ?? "",
      };
    })
    .filter(
      (i) =>
        i.code || i.name || i.spec || Number(i.qty) > 0 || i.maker || i.remark
    );

  const goCreateCK = () => {
    if (!dns) return;
    navigate(`/project/${projectId}/material/CK/new?qc_dns=${dns}`);
  };

  /* =====================================================
     CK 승인 여부 + 마지막 승인자 + CK DNS
  ===================================================== */
  const [ckApproved, setCkApproved] = useState(false);
  const [finalApproverId, setFinalApproverId] = useState(null);
  const [ckDns, setCkDns] = useState(null);

  useEffect(() => {
    if (!dns) return;

    (async () => {
      try {
        const res = await api.get("material/docs/", {
          params: {
            project: projectId,
            doc_type: "CK",
            qc_dns: dns,
            page_size: 1,
            ordering: "-created_at",
          },
        });

        const ck = res.data?.results?.[0];
        if (!ck) return;

        setCkDns(ck.dns);
        setCkApproved(ck.status === "approved");

        if (ck.status !== "approved") return;

        const lineRes = await api.get("approval/lines/view", {
          params: { dns: ck.dns },
        });

        const approvedLines = (lineRes.data?.approval_lines || [])
          .filter((l) => l.is_approved === "approved")
          .sort((a, b) => b.order - a.order);

        if (approvedLines.length) {
          setFinalApproverId(String(approvedLines[0].approver_id));
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [dns, projectId]);

  const canWriteNotice =
    ckApproved && String(user.id) === String(finalApproverId);

  /* =====================================================
     통보서 STATE
  ===================================================== */
  const [requestPeople, setRequestPeople] = useState([]);
  const [notice, setNotice] = useState({
    inspect_date: "",
    result: "",
    notice_date: "",
  });

  useEffect(() => {
    const saved = data?.header?.notice;
    if (!saved) return;

    setNotice({
      inspect_date: saved.inspect_date || "",
      result: saved.result || "",
      notice_date: saved.notice_date || "",
    });
  }, [data]);

  const savedNoticeExists = !!data?.header?.notice;

  /* =====================================================
     통보 저장
  ===================================================== */
  const [savingNotice, setSavingNotice] = useState(false);

  const saveNotice = async () => {
    if (!notice.inspect_date || !notice.result || !notice.notice_date) {
      alert("검수일시, 검수결과, 통보일자를 모두 입력하세요.");
      return;
    }

    if (!requestPeople?.length) {
      alert("수신자를 선택하세요.");
      return;
    }

    try {
      setSavingNotice(true);

      // ✅ QC 원본 문서 재조회
      const qcRes = await getMaterialDoc({
        dns,
        project: projectId,
      });

      // ✅ 핵심: body 제거 + 순수 라인으로 정규화
      const normalizedLines = (qcRes.lines || []).map((l) => {
        if (l.body && typeof l.body === "object") {
          return { ...l.body };
        }

        const { body, id, seq, doc_dns, ...rest } = l;
        return rest;
      });

      await api.post("material/docs/save_full/", {
        values: {
          project: projectId,
          module: "material",
          doc_type: "QC",
          dns,
          header: {
            ...(qcRes.header || {}),
            notice: {
              inspect_date: notice.inspect_date,
              result: notice.result,
              notice_date: notice.notice_date,
              receiver_ids: requestPeople.map((p) => p.id),
              receiver_names: requestPeople.map((p) => p.name || p.user?.name),
              writer_id: user.id,
              writer_name: user.name,
              ck_dns: ckDns,
            },
          },
          lines: normalizedLines, // ✅ body 없는 깨끗한 lines
        },
      });

      alert("자재 검수 결과 통보가 저장되었습니다.");
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert("통보 저장 실패");
    } finally {
      setSavingNotice(false);
    }
  };

  return (
    <div className="space-y-6 text-xs">
      <div className="text-sm">
        아래 자재 반입 내역에 대해 검수를 요청합니다.
      </div>

      {canCreateCK && (
        <div className="flex justify-end">
          <button className="btn-basic-sm" onClick={goCreateCK}>
            검수 체크리스트 생성하기
          </button>
        </div>
      )}

      {/* QC 항목 테이블 */}
      <table className="doc-table w-full">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th">자재코드</th>
            <th className="doc-th">자재명</th>
            <th className="doc-th">규격</th>
            <th className="doc-th">단위</th>
            <th className="doc-th">수량</th>
            <th className="doc-th">제조회사</th>
            <th className="doc-th-end">비고</th>
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((i, idx) => (
              <tr key={idx}>
                <td className="doc-td">{i.code}</td>
                <td className="doc-td">{i.name}</td>
                <td className="doc-td">{i.spec}</td>
                <td className="doc-td text-center">{i.unit}</td>
                <td className="doc-td text-right">{i.qty}</td>
                <td className="doc-td">{i.maker}</td>
                <td className="doc-td">{i.remark}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="doc-td text-center text-gray-400">
                항목 없음
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ✅ 작성자 사인 (API 기반, 최종 정답) */}
      {dns && <SignTableDocCreator dns={dns} />}

      {/* ===============================
          자재 검수 결과 통보서
      =============================== */}
      {ckApproved && (
        <>
          <hr className="my-8 border-dashed" />

          <div className="text-center text-sm font-bold">
            자재 검수 결과 통보
          </div>

          {canWriteNotice && !savedNoticeExists && (
            <>
              <table className="doc-table w-full text-sm mt-4">
                <tbody>
                  <tr>
                    <td className="tbl-label-bg">요청번호</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      {ckDns || "-"}
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">수신</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      <PeopleCellLoader
                        single
                        dns={dns}
                        value={requestPeople}
                        onChange={setRequestPeople}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">PM 단장</td>
                    <td className="tbl-value-bg">{user.name}</td>
                    <td className="tbl-label-bg">검수 일시</td>
                    <td className="tbl-value-bg">
                      <input
                        type="date"
                        className="input-base w-full"
                        value={notice.inspect_date}
                        onChange={(e) =>
                          setNotice((p) => ({
                            ...p,
                            inspect_date: e.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">검수 결과</td>
                    <td className="tbl-value-bg">
                      <textarea
                        className="input-base w-full"
                        rows={3}
                        value={notice.result}
                        onChange={(e) =>
                          setNotice((p) => ({
                            ...p,
                            result: e.target.value,
                          }))
                        }
                      />
                    </td>

                    <td className="tbl-label-bg">통보 일자</td>
                    <td className="tbl-value-bg">
                      <input
                        type="date"
                        className="input-base w-full"
                        value={notice.notice_date}
                        onChange={(e) =>
                          setNotice((p) => ({
                            ...p,
                            notice_date: e.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="flex justify-end mt-4">
                <button
                  className="btn-basic-sm"
                  disabled={savingNotice}
                  onClick={saveNotice}
                >
                  {savingNotice ? "저장 중..." : "저장하기"}
                </button>
              </div>
            </>
          )}
          {savedNoticeExists && (
            <>
              <table className="doc-table w-full text-sm mt-4">
                <tbody>
                  <tr>
                    <td className="tbl-label-bg">요청번호</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      {data?.header?.notice?.ck_dns || ckDns || "-"}
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">수신</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      {(data?.header?.notice?.receiver_names || []).join(
                        ", "
                      ) || "-"}
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">PM 단장</td>
                    <td className="tbl-value-bg">
                      {data?.header?.notice?.writer_name || "-"}
                    </td>

                    <td className="tbl-label-bg">검수 일시</td>
                    <td className="tbl-value-bg">
                      {data?.header?.notice?.inspect_date || "-"}
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">검수 결과</td>
                    <td className="tbl-value-bg">
                      {data?.header?.notice?.result || "-"}
                    </td>

                    <td className="tbl-label-bg">통보 일자</td>
                    <td className="tbl-value-bg">
                      {data?.header?.notice?.notice_date || "-"}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 통보서 작성자 사인 */}
              {ckDns && (
                <div className="mt-6">
                  <SignTableDocSingle dns={ckDns} onlyLastApprover />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
